import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from '../api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setUser(false);
        setLoading(false);
        return;
      }
      const { data } = await authAPI.getMe();
      setUser(data);
    } catch (error) {
      localStorage.removeItem('access_token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (loginId, password) => {
    const { data } = await authAPI.login({ login_id: loginId, password });
    if (data.access_token) {
      localStorage.setItem('access_token', data.access_token);
    }
    setUser(data);
    // Re-hydrate full profile (includes calling_controls & other fields the login payload may omit)
    try {
      const { data: me } = await authAPI.getMe();
      setUser(me);
      return me;
    } catch (e) {
      return data;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      // Silent
    }
    localStorage.removeItem('access_token');
    setUser(false);
  }, []);

  const hasPermission = useCallback((perm) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return (user.permissions || []).includes(perm);
  }, [user]);

  const hasAnyPermission = useCallback((perms) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    const userPerms = user.permissions || [];
    return perms.some(p => userPerms.includes(p));
  }, [user]);

  const updateUser = useCallback((patch) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, checkAuth, hasPermission, hasAnyPermission, updateUser }),
    [user, loading, login, logout, checkAuth, hasPermission, hasAnyPermission, updateUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
